import { SECTION_TYPE_LABELS, createSectionDraft, getLeadFormPreset } from './registry'

function buildAsset(fileId, sourceType = 'upload') {
  if (!fileId) return {}
  return { sourceType, fileId }
}

function upgradeLegacySectionCopy(section = {}) {
  const content = section?.content && typeof section.content === 'object' ? { ...section.content } : {}
  if (section?.type === 'hero' && content.ctaText === '지원하기') {
    content.ctaText = '입사문의하기'
  }
  if (section?.type === 'lead-form') {
    if (content.title === '지원하기' || content.title === '입사지원하기') {
      content.title = '입사문의하기'
    }
    if (content.submitText === '지원하기' || content.submitText === '간편 지원하기') {
      content.submitText = content.submitText === '간편 지원하기' ? '간편 입사문의하기' : '입사문의하기'
    }
    if (content.successMessage === '지원이 접수되었습니다.' || content.successMessage === '지원이 접수되었습니다. 빠르게 연락드리겠습니다.') {
      content.successMessage = '입사문의가 접수되었습니다. 빠르게 연락드리겠습니다.'
    }
  }
  return content
}

function withIdentity(section, index) {
  return {
    id: section?.id || `section-${index + 1}`,
    enabled: section?.enabled !== false,
    order: Number(section?.order ?? index),
    type: section?.type || 'custom',
    templateCode: section?.templateCode || `${section?.type || 'custom'}.default`,
    content: upgradeLegacySectionCopy(section),
    assets: section?.assets && typeof section.assets === 'object' ? section.assets : {},
    style: section?.style && typeof section.style === 'object' ? section.style : {},
  }
}

export function deriveLegacySections(raw = {}) {
  const sections = []
  if (raw.hero?.title || raw.hero?.subtitle || raw.hero?.imageFileId) {
    const base = createSectionDraft('hero', 'hero.centered-v1')
    sections.push(withIdentity({
      ...base,
      content: {
        ...base.content,
        title: raw.hero?.title || '',
        subtitle: raw.hero?.subtitle || '',
        ctaText: raw.hero?.ctaText || '입사문의하기',
        ctaLink: raw.hero?.ctaLink || '#lead-form',
      },
      assets: {
        ...base.assets,
        backgroundImage: buildAsset(raw.hero?.imageFileId),
      },
    }, sections.length))
  }
  if (raw.about?.title || raw.about?.content || raw.about?.imageFileId) {
    const base = createSectionDraft('ga-intro', 'ga-intro.media-v1')
    sections.push(withIdentity({
      ...base,
      content: {
        ...base.content,
        title: raw.about?.title || '',
        body: raw.about?.content || '',
      },
      assets: {
        ...base.assets,
        mainImage: buildAsset(raw.about?.imageFileId),
      },
      style: {
        ...base.style,
        ...raw.style,
      },
    }, sections.length))
  }
  if (Array.isArray(raw.stats) && raw.stats.length > 0) {
    const base = createSectionDraft('stats', 'stats.grid-v1')
    sections.push(withIdentity({
      ...base,
      content: {
        ...base.content,
        items: raw.stats,
      },
    }, sections.length))
  }
  if (raw.ceo?.name || raw.ceo?.message || raw.ceo?.imageFileId) {
    const base = createSectionDraft('ceo-message', 'ceo-message.split-v1')
    sections.push(withIdentity({
      ...base,
      content: {
        ...base.content,
        title: raw.ceo?.name || '',
        subtitle: raw.ceo?.title || '',
        body: raw.ceo?.message || '',
      },
      assets: {
        ...base.assets,
        mainImage: buildAsset(raw.ceo?.imageFileId),
      },
    }, sections.length))
  }
  if (raw.contact?.phone || raw.contact?.email || raw.contact?.kakaoLink || raw.contact?.address) {
    const base = createSectionDraft('contact', 'contact.centered-v1')
    sections.push(withIdentity({
      ...base,
      content: {
        ...base.content,
        address: raw.contact?.address || '',
        phone: raw.contact?.phone || '',
        email: raw.contact?.email || '',
        kakaoLink: raw.contact?.kakaoLink || '',
      },
    }, sections.length))
  }
  return sections
}

export function normalizeLandingPageData(raw = {}) {
  const sections = Array.isArray(raw.sections) && raw.sections.length > 0
    ? raw.sections.map(withIdentity).sort((a, b) => a.order - b.order)
    : deriveLegacySections(raw)

  return {
    id: raw.id || null,
    orgId: raw.orgId || null,
    isPublished: Boolean(raw.isPublished),
    templateId: raw.templateId || 'default',
    meta: raw.meta || { title: '', description: '', keywords: '' },
    style: raw.style || { primaryColor: '#0f172a' },
    logoFileId: raw.logoFileId || '',
    sections,
  }
}

export function cloneSection(section, nextId) {
  return {
    ...section,
    id: nextId,
    order: Number(section?.order || 0),
    content: JSON.parse(JSON.stringify(section?.content || {})),
    assets: JSON.parse(JSON.stringify(section?.assets || {})),
    style: JSON.parse(JSON.stringify(section?.style || {})),
  }
}

export function updateSectionTemplate(section, templateCode) {
  if (!section) return section
  if (section.type !== 'lead-form') {
    return { ...section, templateCode }
  }
  return {
    ...section,
    templateCode,
    content: {
      ...(section.content || {}),
      fields: getLeadFormPreset(templateCode),
    },
  }
}

export function buildNavigationFromSections(sections = []) {
  const visible = sections
    .filter((section) => section?.enabled !== false)
    .filter((section) => section?.type !== 'hero')
    .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0))

  return visible.map((section, index) => ({
    id: section.id || `${section.type}-${index}`,
    label: String(
      section?.content?.navLabel
      || SECTION_TYPE_LABELS[section?.type]
      || section?.content?.title
      || section?.type
      || '섹션'
    ),
    type: 'section',
    sectionRef: section.id || `${section.type}-${index}`,
    enabled: true,
    order: index,
  }))
}

export function buildLegacyPayloadFields(sections = []) {
  const result = {
    hero: { title: '', subtitle: '', imageFileId: '', ctaText: '', ctaLink: '' },
    about: { title: '', content: '', imageFileId: '' },
    stats: [],
    ceo: { name: '', title: '', message: '', imageFileId: '' },
    contact: { address: '', phone: '', email: '', kakaoLink: '' },
    additionalSections: [],
  }

  sections.forEach((section) => {
    if (!section || section.enabled === false) return
    const content = section.content || {}
    const assets = section.assets || {}
    if (section.type === 'hero' && !result.hero.title && !result.hero.subtitle) {
      result.hero = {
        title: content.title || '',
        subtitle: content.subtitle || '',
        imageFileId: assets.backgroundImage?.fileId || '',
        ctaText: content.ctaText || '',
        ctaLink: content.ctaLink || '',
      }
      return
    }
    if (section.type === 'ga-intro' && !result.about.title && !result.about.content) {
      result.about = {
        title: content.title || '',
        content: content.body || '',
        imageFileId: assets.mainImage?.fileId || '',
      }
      return
    }
    if (section.type === 'stats' && result.stats.length === 0) {
      result.stats = Array.isArray(content.items) ? content.items : []
      return
    }
    if (section.type === 'ceo-message' && !result.ceo.name && !result.ceo.message) {
      result.ceo = {
        name: content.title || '',
        title: content.subtitle || '',
        message: content.body || '',
        imageFileId: assets.mainImage?.fileId || '',
      }
      return
    }
    if (section.type === 'contact' && !result.contact.phone && !result.contact.email && !result.contact.kakaoLink) {
      result.contact = {
        address: content.address || '',
        phone: content.phone || '',
        email: content.email || '',
        kakaoLink: content.kakaoLink || '',
      }
      return
    }
    result.additionalSections.push({
      type: section.type,
      templateCode: section.templateCode,
      title: content.title || '',
      subtitle: content.subtitle || '',
      body: content.body || '',
      items: content.items || [],
      fields: content.fields || [],
      assets,
      style: section.style || {},
    })
  })

  return result
}
